import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, Subject, Subscription, timer, BehaviorSubject } from 'rxjs';
import { catchError, delayWhen, filter, first, map, retryWhen, switchAll, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

import { environment } from 'src/environments/environment';
import { CustomAuthService } from '../auth/custom-auth.service';
import { Resource } from '../model/resource';

interface WebSocketResponse {
  data: Resource[];
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private tokenSubscription: Subscription;
  private socket: WebSocketSubject<WebSocketResponse>;
  private messagesSubject = new Subject<Observable<WebSocketResponse>>();

  public messages = this.messagesSubject.pipe(switchAll());

  constructor(private auth: CustomAuthService) {
    this.tokenSubscription = this.auth.token.pipe(filter(value => value != null))
      .subscribe(token => {
        // Close current connection on new token.
        this.disconnect();

        // Do not try to connect with empty token (happens after logout).
        if (token != null) {
          this.connect(auth.envConfig$.getValue().baseWSUrl + '?type=app&componentId=' + token.accessToken);
        }
      });
  }

  getDataStream(dataType: string | string[]): Observable<Resource[]> {
    return this.messages.pipe(
      map(response => response.data),
      filter(data => Array.isArray(dataType)
        ? data.some(resource => dataType.includes(resource.type))
        : data.some(resource => dataType === resource.type))
    );
  }

  ngOnDestroy() {
    this.disconnect();
    this.tokenSubscription.unsubscribe();
  }

  private connect(url: string) {
    if (this.socket && !this.socket.closed) { return; }

    this.socket = webSocket(url);

    // TODO: Check if reconnection happens after normal closure (it shouldn't)
    const messages = this.socket.pipe(
      retryWhen(errors => errors.pipe(
        tap(val => console.log('[WebSocket] Try to reconnect', val)),
        delayWhen(_ => timer(1000))
      )),
      tap({ error: error => console.log('[WebSocket] Error', error) }),
      catchError(_ => EMPTY)
    );
    this.messagesSubject.next(messages);

    console.log('[WebSocket] Connected to ' + url);
  }

  private disconnect() {
    if (this.socket) {
      this.socket.complete();
      this.socket = undefined;
      console.log('[WebSocket] Disconnected.');
    }
  }

}
